import type React from 'react';
import { useCallback, useEffect, useRef } from 'react';

interface UseWindowScrollerListArg {
  listInstance: React.RefObject<HTMLElement>;
  outerListRef: React.RefObject<HTMLElement>;
}

function getScrollOffset(element = { scrollTop: 0, scrollLeft: 0 }) {
  return {
    top: element.scrollTop,
    left: element.scrollLeft,
  };
}

function getPositionOffset(listEl: HTMLElement, scrollableEl: HTMLElement) {
  const scrollOffset = getScrollOffset(scrollableEl);
  const elementRect = listEl.getBoundingClientRect();
  const containerRect = scrollableEl.getBoundingClientRect();
  return {
    top: elementRect.top + scrollOffset.top - containerRect.top,
    left: elementRect.left + scrollOffset.top - containerRect.left,
  };
}

function isParent(parentNode: EventTarget | null, childNode: HTMLElement | null): boolean {
  return (
    childNode?.parentNode === (parentNode as ParentNode) ||
    (!!childNode?.parentNode && isParent(parentNode, childNode.parentNode as HTMLElement))
  );
}

export const useWindowScrollerList = ({ listInstance, outerListRef }: UseWindowScrollerListArg) => {
  const scrollableParent = useRef<HTMLElement | false>();
  const listOffset = useRef<ReturnType<typeof getPositionOffset>>();

  const getElementOffset = useCallback(() => {
    if (outerListRef.current && scrollableParent.current) {
      listOffset.current = getPositionOffset(outerListRef.current, scrollableParent.current);
    }
  }, [outerListRef]);

  const handleScroll: EventListener = useCallback(
    (e) => {
      if (!scrollableParent.current) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const containerTarget = e.target === document ? document.body : e.target;
        scrollableParent.current = isParent(containerTarget, outerListRef.current) && (containerTarget as HTMLElement);
      }

      if (!scrollableParent.current) return;

      getElementOffset();

      const scrollOffset = getScrollOffset(scrollableParent.current);
      const scrollTop = Math.max(0, scrollOffset.top - (listOffset.current?.top ?? 0));
      if (listInstance.current) listInstance.current.scrollTo({ top: scrollTop });
    },
    [getElementOffset, listInstance, outerListRef],
  );

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', getElementOffset, true);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', getElementOffset, true);
    };
  }, [getElementOffset, handleScroll]);
};

export default useWindowScrollerList;
